import React, { Fragment, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import type { TariffHistoryItem } from '../../../apis/types'
import { createTariffHistoryApi } from '../../../apis/apis'
import { formatDateForApi, formatUTCDetailedDate } from '../../../util/string'
import { pickSelectedHistoryCuid, sortHistoriesNewestFirst } from './tariffHistoryUtils'

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

export const NewRatesetModal: React.FC<{
    open: boolean
    tariffCuid: string
    histories: TariffHistoryItem[]
    onClose: () => void
    onCreated: (historyCuid: string) => void
}> = ({ open, tariffCuid, histories, onClose, onCreated }) => {
    const ordered = useMemo(() => sortHistoriesNewestFirst(histories), [histories])
    const defaultCopy = pickSelectedHistoryCuid(ordered) ?? ordered[0]?.cuid ?? ''
    const [effectiveDate, setEffectiveDate] = useState(() => formatDateForApi(new Date()) ?? '')
    const [copyFromHistoryCuid, setCopyFromHistoryCuid] = useState(defaultCopy)
    const [notes, setNotes] = useState('')
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const reset = (): void => {
        setEffectiveDate(formatDateForApi(new Date()) ?? '')
        setCopyFromHistoryCuid(defaultCopy)
        setNotes('')
        setReason('')
        setError(null)
    }

    const close = (): void => {
        reset()
        onClose()
    }

    const handleCreate = (): void => {
        if (effectiveDate.trim() === '' || reason.trim() === '') return
        setSaving(true)
        setError(null)
        void createTariffHistoryApi(tariffCuid, {
            effectiveDate,
            notes: notes.trim() === '' ? null : notes.trim(),
            copyFromHistoryCuid: copyFromHistoryCuid === '' ? undefined : copyFromHistoryCuid,
            reason: reason.trim(),
        })
            .then((detail) => {
                onCreated(detail.selectedHistoryCuid ?? copyFromHistoryCuid)
                close()
            })
            .catch(() => setError('Failed to create rateset.'))
            .finally(() => setSaving(false))
    }

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={close}>
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                            <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                New rateset
                            </Dialog.Title>
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                                Creates a Pending rateset. Activate it to close the current Active
                                rateset the day before this effective date.
                            </p>
                            <div className="mt-4 space-y-3">
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Effective date
                                    <input
                                        type="date"
                                        className={`${inputClass} mt-1`}
                                        value={effectiveDate}
                                        onChange={(e) => setEffectiveDate(e.target.value)}
                                    />
                                </label>
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Copy rates from
                                    <select
                                        className={`${inputClass} mt-1`}
                                        value={copyFromHistoryCuid}
                                        onChange={(e) => setCopyFromHistoryCuid(e.target.value)}
                                    >
                                        <option value="">None</option>
                                        {ordered.map((history) => (
                                            <option key={history.cuid} value={history.cuid}>
                                                {formatUTCDetailedDate(new Date(history.effectiveDate))}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Notes
                                    <textarea
                                        className={`${inputClass} mt-1`}
                                        rows={2}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </label>
                                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                    Reason <span className="font-normal text-slate-500">(required)</span>
                                    <textarea
                                        className={`${inputClass} mt-1`}
                                        rows={2}
                                        maxLength={500}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </label>
                            </div>
                            {error != null ? (
                                <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
                            ) : null}
                            <div className="mt-5 flex justify-end gap-2">
                                <button
                                    type="button"
                                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                                    onClick={close}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                    onClick={handleCreate}
                                    disabled={saving || reason.trim() === '' || effectiveDate.trim() === ''}
                                >
                                    {saving ? 'Creating…' : 'Create rateset'}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default NewRatesetModal
